/**
 * Initialize accessible tab components.
 * This version fixes invalid ARIA values by generating unique IDs for both tabs and panels.
 * Each <button role="tab"> controls one <div role="tabpanel"> via valid aria-controls / aria-labelledby links.
 */
export function initSimpleTabs($containers) {
	$containers.each(function () {
		const $tabList = $(this);
		const $tabs = $tabList.find('[role="tab"]');
		const tabGroupId = Math.random().toString(36).substring(2, 9); // unique group ID to avoid collisions

		// Respect a tab already marked active in server-rendered HTML instead of always
		// defaulting to the first tab — some pages pre-select a non-first tab by design.
		let defaultIndex = $tabs.index($tabs.filter('.active').first());
		if (defaultIndex === -1) defaultIndex = 0;

		$tabs.each(function (index) {
			const $tab = $(this);
			const panelClass = $tab.attr('data-tab'); // class selector for the target panel
			const $panel = $(`.${panelClass}`);
			const isDefault = index === defaultIndex;

			// Generate unique IDs for both tab and panel
			const tabId = `${tabGroupId}-tab-${index}`;
			const panelId = `${tabGroupId}-panel-${index}`;

			// === Assign valid ARIA attributes ===
			$tab.attr({
				id: tabId,
				'aria-controls': panelId, // must reference a real element ID
				'aria-selected': isDefault ? 'true' : 'false',
				tabindex: isDefault ? '0' : '-1'
			});

			$panel.attr({
				id: panelId,
				role: 'tabpanel',
				'aria-labelledby': tabId,
				tabindex: '0'
			});

			// === Initial visibility ===
			if (isDefault) {
				$panel.removeAttr('hidden').addClass('in');
			} else {
				$panel.attr('hidden', '').removeClass('in');
			}
		});

		// === CLICK HANDLER ===
		$tabs.on('click', function (e) {
			e.preventDefault();
			switchTab($(this), $tabs);
		});

		// === KEYBOARD NAVIGATION ===
		// Supports ArrowLeft / ArrowRight / Home / End keys for better accessibility
		$tabList.on('keydown', function (e) {
			const key = e.key;
			const $current = $tabs.filter('[aria-selected="true"]');
			let index = $tabs.index($current);

			if (['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(key)) {
				e.preventDefault();

				if (key === 'ArrowRight') index = (index + 1) % $tabs.length;
				else if (key === 'ArrowLeft') index = (index - 1 + $tabs.length) % $tabs.length;
				else if (key === 'Home') index = 0;
				else if (key === 'End') index = $tabs.length - 1;

				switchTab($tabs.eq(index), $tabs);
				$tabs.eq(index).focus(); // move keyboard focus to the new tab
			}
		});
	});
}

/**
 * Switch between tabs and their associated panels.
 * Handles ARIA updates, visibility, and keyboard focus state.
 */
function switchTab($newTab, $tabs) {
	const $tabList = $newTab.closest('[role="tablist"]');
	const $container = $tabList.closest('.section, .features--tabs, body');
	const controlledPanelId = $newTab.attr('aria-controls');

	// === Deactivate all tabs and panels ===
	$tabs.each(function () {
		const $tab = $(this);
		const panelId = $tab.attr('aria-controls');
		const $panel = $container.find(`#${panelId}`);

		$tab.attr({ 'aria-selected': 'false', tabindex: '-1' }).removeClass('active');
		$panel.attr('hidden', '').removeClass('in');
	});

	// === Activate the newly selected tab ===
	$newTab.attr({ 'aria-selected': 'true', tabindex: '0' }).addClass('active');

	// === Show the corresponding panel ===
	const $newPanel = $container.find(`#${controlledPanelId}`);
	$newPanel.removeAttr('hidden').addClass('in');
}
